import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import { api, ApiError } from "../api";
import { Card, InlineAlert } from "../company-ui";
import { NOTICES_KEY } from "../shell/NoticesBell";

interface Failure { id: string; job: string; plural: string; title: string; line: string; link: string | null; lastFailedAt: string; attempts: number }

const when = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

/** Several failures of one kind fold into one row, "<n> syncs didn't finish" (admin.md B, v31 attnGroups31). */
export function groupFailures(items: Failure[]): { job: string; title: string | null; items: Failure[] }[] {
  const by = new Map<string, Failure[]>();
  for (const f of items) by.set(f.job, [...(by.get(f.job) ?? []), f]);
  return [...by.entries()].map(([job, xs]) => ({ job, title: xs.length > 1 ? `${xs.length} ${xs[0]!.plural} didn't finish` : null, items: xs }));
}

/**
 * Administration › Integrations › Needs attention (ALN-26, approved v31 screen): every open job
 * failure in the winery. Dismiss hides it for you only, here and under your bell. Retrying happens on
 * the owning page (the link), never from a generic button here.
 */
export function NeedsAttention() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["job-failures"], queryFn: () => api<{ failures: Failure[] }>("/job-failures") });
  const dismiss = useMutation({
    mutationFn: (id: string) => api(`/job-failures/${id}/dismiss`, { body: {} }),
    onSettled: () => { void qc.invalidateQueries({ queryKey: ["job-failures"] }); void qc.invalidateQueries({ queryKey: NOTICES_KEY }); },
  });
  const items = q.data?.failures ?? [];
  const row = (f: Failure) => (
    <div className="attn__row" key={f.id}>
      <p className="m0"><b>{f.title}</b></p>
      <p className="m0">{f.line}</p>
      <span className="small muted">{when(f.lastFailedAt)}</span>
      <div className="actions">
        {f.link && <Link className="btn btn--sm" href={f.link}>Open<span className="sr"> {f.title}</span></Link>}
        <button className="btn btn--sm btn--ghost" type="button" disabled={dismiss.isPending} onClick={() => dismiss.mutate(f.id)}>Dismiss<span className="sr"> {f.title}</span></button>
      </div>
    </div>
  );
  return (
    <Card title="Needs attention" headingId="attn-h">
      {q.isError && <div className="card__c"><InlineAlert variant="error" title={q.error instanceof ApiError ? q.error.message : "Needs attention didn't load. Try again."} /></div>}
      {dismiss.isError && <div className="card__c"><InlineAlert variant="error" title="That didn't dismiss. Try again." /></div>}
      {q.isSuccess && !items.length ? (
        <div className="empty"><p className="empty__t">Nothing needs attention.</p></div>
      ) : (
        <div className="card__c stackgap">
          {groupFailures(items).map(g => g.title ? (
            <details className="attn__grp" key={g.job}>
              <summary><b>{g.title}</b></summary>
              <div className="stackgap">{g.items.map(row)}</div>
            </details>
          ) : row(g.items[0]!))}
        </div>
      )}
    </Card>
  );
}
